import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { COLORS } from "../data.js";
import { canvasTexture } from "./textures.js";

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const UNIT_CYLINDER = new THREE.CylinderGeometry(1, 1, 1, 10);

const MATERIALS = {
  cream: new THREE.MeshStandardMaterial({ color: 0xfff7e8, roughness: 0.83 }),
  whiteFabric: new THREE.MeshStandardMaterial({ color: 0xf6f2e8, roughness: 0.92, side: THREE.DoubleSide }),
  navy: new THREE.MeshStandardMaterial({ color: COLORS.navy, roughness: 0.68 }),
  orange: new THREE.MeshStandardMaterial({ color: COLORS.orange, roughness: 0.72 }),
  green: new THREE.MeshStandardMaterial({ color: COLORS.green, roughness: 0.78 }),
  sky: new THREE.MeshStandardMaterial({ color: COLORS.sky, roughness: 0.76 }),
  yellow: new THREE.MeshStandardMaterial({ color: COLORS.yellow, roughness: 0.76 }),
  wood: new THREE.MeshStandardMaterial({ color: 0xa97548, roughness: 0.94 }),
  paleWood: new THREE.MeshStandardMaterial({ color: 0xd0ab78, roughness: 0.92 }),
  metal: new THREE.MeshStandardMaterial({ color: 0x858b8e, metalness: 0.72, roughness: 0.32 }),
  dark: new THREE.MeshStandardMaterial({ color: 0x202d35, roughness: 0.66 }),
  rubber: new THREE.MeshStandardMaterial({ color: 0x171d20, roughness: 0.92 }),
};

function box(group, size, position, material, cast = true, receive = true) {
  const mesh = new THREE.Mesh(UNIT_BOX, material);
  mesh.scale.set(...size);
  mesh.position.set(...position);
  mesh.castShadow = cast;
  mesh.receiveShadow = receive;
  group.add(mesh);
  return mesh;
}

function rodGeometry(from, to, radius = 0.06, segments = 7) {
  const start = new THREE.Vector3(...from);
  const end = new THREE.Vector3(...to);
  const direction = end.clone().sub(start);
  const center = start.clone().add(end).multiplyScalar(0.5);
  const geometry = new THREE.CylinderGeometry(radius, radius, direction.length(), segments);
  const rotation = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  geometry.applyMatrix4(new THREE.Matrix4().compose(center, rotation, new THREE.Vector3(1, 1, 1)));
  return geometry;
}

function mergedRods(lines, material, radius = 0.06) {
  const geometry = mergeGeometries(lines.map(([a, b, r]) => rodGeometry(a, b, r ?? radius)), false);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function addTextPanel(group, text, subtitle, size, position, rotationY = 0, colors = {}) {
  const texture = canvasTexture({
    title: text,
    subtitle,
    background: colors.background ?? "#082d57",
    accent: colors.accent ?? "#ef5a17",
    width: 1024,
    height: 384,
  });
  const panel = new THREE.Mesh(
    new THREE.PlaneGeometry(...size),
    new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false }),
  );
  panel.position.set(...position);
  panel.rotation.y = rotationY;
  group.add(panel);
  return panel;
}

function setInstance(mesh, index, position, scale, rotationY = 0, rotationX = 0, rotationZ = 0) {
  const dummy = new THREE.Object3D();
  dummy.position.set(...position);
  dummy.rotation.set(rotationX, rotationY, rotationZ);
  dummy.scale.set(...scale);
  dummy.updateMatrix();
  mesh.setMatrixAt(index, dummy.matrix);
}

function localToWorld(placement, local) {
  const [x, y, z] = local;
  const c = Math.cos(placement.rotation);
  const s = Math.sin(placement.rotation);
  return [placement.x + x * c + z * s, y, placement.z - x * s + z * c];
}

function createCanopyField() {
  const group = new THREE.Group();
  const categories = [
    { label: "生活選物", material: MATERIALS.orange, color: "#ef5a17" },
    { label: "親子手作", material: MATERIALS.green, color: "#61aa45" },
    { label: "美味市集", material: MATERIALS.sky, color: "#70bde6" },
    { label: "合作夥伴", material: MATERIALS.yellow, color: "#f9b71b" },
  ];
  const placements = [];
  for (let i = 0; i < 6; i += 1) placements.push({ x: -49 + i * 3.55, z: -25, rotation: 0, category: i % 3 });
  for (let i = 0; i < 7; i += 1) placements.push({ x: 48, z: -20 + i * 3.65, rotation: Math.PI / 2, category: (i + 1) % 3 });
  for (let i = 0; i < 5; i += 1) placements.push({ x: 30 + i * 3.65, z: 31, rotation: 0.15, category: 3 });

  const roofGeometry = new THREE.ConeGeometry(2.12, 0.95, 4);
  const roofs = new THREE.InstancedMesh(roofGeometry, MATERIALS.whiteFabric, placements.length);
  const legs = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.metal, placements.length * 4);
  const weights = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.dark, placements.length * 4);
  const counters = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.paleWood, placements.length);
  const valances = categories.map(category => new THREE.InstancedMesh(UNIT_BOX, category.material, placements.filter(p => p.category === categories.indexOf(category)).length));
  const signGeometry = new THREE.PlaneGeometry(2.45, 0.48);
  const signs = categories.map(category => {
    const texture = canvasTexture({ title: category.label, background: "#082d57", accent: category.color, width: 768, height: 192 });
    return new THREE.InstancedMesh(signGeometry, new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false }), placements.filter(p => p.category === categories.indexOf(category)).length);
  });
  const categoryIndices = [0, 0, 0, 0];

  placements.forEach((placement, index) => {
    setInstance(roofs, index, [placement.x, 2.93, placement.z], [1, 1, 1], placement.rotation + Math.PI / 4);
    const corners = [[-1.45, 1.2, -1.45], [1.45, 1.2, -1.45], [-1.45, 1.2, 1.45], [1.45, 1.2, 1.45]];
    corners.forEach((corner, cornerIndex) => {
      setInstance(legs, index * 4 + cornerIndex, localToWorld(placement, corner), [0.065, 2.4, 0.065], placement.rotation);
      const weightPosition = localToWorld(placement, [corner[0], 0.11, corner[2]]);
      setInstance(weights, index * 4 + cornerIndex, weightPosition, [0.28, 0.22, 0.28], placement.rotation);
    });
    setInstance(counters, index, localToWorld(placement, [0, 0.78, 0.48]), [2.45, 0.13, 0.72], placement.rotation);
    const catIndex = placement.category;
    const localIndex = categoryIndices[catIndex]++;
    setInstance(valances[catIndex], localIndex, localToWorld(placement, [0, 2.45, -1.5]), [3.05, 0.36, 0.08], placement.rotation);
    setInstance(signs[catIndex], localIndex, localToWorld(placement, [0, 2.22, -1.555]), [1, 1, 1], placement.rotation + Math.PI);
  });

  for (const mesh of [roofs, legs, weights, counters, ...valances, ...signs]) {
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = mesh === roofs || mesh === counters;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  const flagGeometry = new THREE.BufferGeometry();
  flagGeometry.setAttribute("position", new THREE.Float32BufferAttribute([-0.12, 0.08, 0, 0.12, 0.08, 0, 0, -0.16, 0], 3));
  flagGeometry.computeVertexNormals();
  const flagMaterials = [MATERIALS.orange, MATERIALS.sky, MATERIALS.yellow];
  const flags = flagMaterials.map((material, colorIndex) => {
    const count = placements.reduce((total, _, index) => total + [0, 1, 2, 0, 1].filter((__, flagIndex) => (index + flagIndex) % 3 === colorIndex).length, 0);
    return new THREE.InstancedMesh(flagGeometry, material, count);
  });
  const flagIndices = [0, 0, 0];
  placements.forEach((placement, placementIndex) => {
    [-1.15, -0.58, 0, 0.58, 1.15].forEach((x, flagIndex) => {
      const colorIndex = (placementIndex + flagIndex) % 3;
      const point = localToWorld(placement, [x, 2.68, -1.6]);
      setInstance(flags[colorIndex], flagIndices[colorIndex]++, point, [1, 1, 1], placement.rotation + Math.PI);
    });
  });
  flags.forEach(flag => { flag.instanceMatrix.needsUpdate = true; group.add(flag); });
  return group;
}

function createStage() {
  const group = new THREE.Group();
  box(group, [6, 0.45, 4], [0, 0.225, 0], MATERIALS.dark);
  box(group, [5.7, 0.08, 3.7], [0, 0.49, 0], MATERIALS.orange, false);

  const trussLines = [];
  const corners = [[-3.45, 0, -2.3], [3.45, 0, -2.3], [-3.45, 0, 2.3], [3.45, 0, 2.3]];
  for (const [x, , z] of corners) trussLines.push([[x, 0, z], [x, 5.1, z], 0.075]);
  trussLines.push(
    [[-3.45, 5.1, -2.3], [3.45, 5.1, -2.3]], [[-3.45, 5.1, 2.3], [3.45, 5.1, 2.3]],
    [[-3.45, 5.1, -2.3], [-3.45, 5.1, 2.3]], [[3.45, 5.1, -2.3], [3.45, 5.1, 2.3]],
  );
  for (let y = 0.55; y < 4.8; y += 0.65) {
    trussLines.push([[-3.45, y, -2.3], [-3.45, y + 0.55, -2.3], 0.035]);
    trussLines.push([[3.45, y + 0.55, -2.3], [3.45, y, -2.3], 0.035]);
  }
  group.add(mergedRods(trussLines, MATERIALS.metal, 0.065));
  box(group, [6.7, 0.12, 4.4], [0, 5.02, 0], MATERIALS.whiteFabric, false);

  addTextPanel(group, "家的萬象生活節", "市集｜樂表演｜趣體驗", [5.15, 2.35], [0, 2.75, -2.25], 0, { background: "#174f8c", accent: "#ef5a17" });
  const speakers = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.dark, 2);
  setInstance(speakers, 0, [-3.85, 2.35, -1.95], [0.62, 1.35, 0.58]);
  setInstance(speakers, 1, [3.85, 2.35, -1.95], [0.62, 1.35, 0.58]);
  speakers.instanceMatrix.needsUpdate = true;
  speakers.castShadow = true;
  group.add(speakers);

  const stairs = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.dark, 3);
  for (let i = 0; i < 3; i += 1) setInstance(stairs, i, [0, 0.08 + i * 0.12, 2.3 + i * 0.28], [1.8 - i * 0.25, 0.16 + i * 0.08, 0.55]);
  stairs.instanceMatrix.needsUpdate = true;
  group.add(stairs);
  return group;
}

function createWelcomeGate() {
  const group = new THREE.Group();
  box(group, [5.8, 0.34, 0.65], [0, 0.17, 0], MATERIALS.paleWood);
  box(group, [0.42, 4.55, 0.48], [-2.55, 2.45, 0], MATERIALS.cream);
  box(group, [0.42, 4.55, 0.48], [2.55, 2.45, 0], MATERIALS.cream);
  box(group, [5.5, 0.42, 0.48], [0, 4.72, 0], MATERIALS.cream);
  box(group, [0.32, 3.65, 0.56], [-1.15, 2.18, -0.02], MATERIALS.orange);
  box(group, [0.32, 3.65, 0.56], [1.15, 2.18, -0.02], MATERIALS.orange);
  box(group, [2.6, 0.32, 0.56], [0, 3.95, -0.02], MATERIALS.orange);
  const doorPivot = new THREE.Group();
  doorPivot.position.set(-1.02, 0.38, 0.05);
  doorPivot.rotation.y = -0.72;
  box(doorPivot, [2.05, 3.4, 0.16], [1.025, 1.7, 0], MATERIALS.orange);
  const handle = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), MATERIALS.dark);
  handle.position.set(1.8, 1.75, 0.12);
  doorPivot.add(handle);
  group.add(doorPivot);
  addTextPanel(group, "歡迎回家", "WELCOME HOME", [3.7, 0.92], [0, 5.32, 0.03], 0, { background: "#082d57", accent: "#ef5a17" });
  const key = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.14, 10, 28), MATERIALS.yellow);
  key.position.set(-3.35, 1.55, 0.15);
  group.add(key);
  box(group, [1.25, 0.2, 0.2], [-3.83, 1.08, 0.15], MATERIALS.yellow);
  return group;
}

function createHousePhotoWall() {
  const group = new THREE.Group();
  box(group, [6.4, 3.9, 0.32], [0, 1.95, 0], MATERIALS.cream);
  box(group, [3.7, 0.3, 0.36], [-1.48, 4.15, 0], MATERIALS.paleWood, true);
  box(group, [3.7, 0.3, 0.36], [1.48, 4.15, 0], MATERIALS.paleWood, true);
  group.children[group.children.length - 2].rotation.z = 0.5;
  group.children[group.children.length - 1].rotation.z = -0.5;
  box(group, [0.4, 1.2, 0.38], [2.35, 4.18, 0], MATERIALS.cream);
  const scenes = [
    ["閱讀時光", "BOOKS", -1.62, 2.7, "#d8b07b"], ["綠意生活", "PLANTS", 1.62, 2.7, "#6e9f77"],
    ["家的味道", "KITCHEN", -1.62, 1.05, "#e9a96b"], ["毛孩日常", "PETS", 1.62, 1.05, "#7fa7b8"],
  ];
  scenes.forEach(([title, subtitle, x, y, accent]) => {
    box(group, [2.72, 1.35, 0.12], [x, y, 0.19], MATERIALS.paleWood, false);
    addTextPanel(group, title, subtitle, [2.45, 1.08], [x, y, 0.265], 0, { background: "#31536c", accent });
  });
  box(group, [1, 0.35, 0.9], [-2.55, 0.18, 0], MATERIALS.dark);
  box(group, [1, 0.35, 0.9], [2.55, 0.18, 0], MATERIALS.dark);
  return group;
}

function createGameKiosk(title, accent, type) {
  const group = new THREE.Group();
  box(group, [3.5, 2.45, 0.25], [0, 1.23, 0], MATERIALS.cream);
  box(group, [3.75, 0.16, 0.6], [0, 0.12, 0], MATERIALS.paleWood);
  addTextPanel(group, title, type, [3.05, 1.65], [0, 1.35, 0.145], 0, { background: "#082d57", accent });
  return group;
}

function createFoodTruck(color) {
  const group = new THREE.Group();
  const bodyMaterial = new THREE.MeshStandardMaterial({ color, roughness: 0.58, metalness: 0.08 });
  box(group, [4.8, 2.45, 2.2], [0.25, 1.65, 0], bodyMaterial);
  box(group, [1.4, 1.7, 2.12], [-2.25, 1.28, 0], bodyMaterial);
  box(group, [1.15, 0.78, 0.06], [-2.26, 1.85, -1.075], new THREE.MeshStandardMaterial({ color: 0x8bb8c8, metalness: 0.2, roughness: 0.16 }), false);
  box(group, [2.15, 1.22, 0.06], [0.75, 1.82, -1.115], MATERIALS.dark, false);
  box(group, [2.65, 0.13, 1.1], [0.75, 2.55, -1.62], MATERIALS.cream);
  addTextPanel(group, "家的餐車", "FAMILY FOOD TRUCK", [2.65, 0.68], [0.35, 3.06, -1.13], 0, { background: "#082d57", accent: "#ef5a17" });
  const wheels = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.46, 0.46, 0.26, 18), MATERIALS.rubber, 4);
  [[-1.55, -1.09], [1.55, -1.09], [-1.55, 1.09], [1.55, 1.09]].forEach(([x, z], index) => {
    setInstance(wheels, index, [x, 0.48, z], [1, 1, 1], 0, Math.PI / 2);
  });
  wheels.instanceMatrix.needsUpdate = true;
  wheels.castShadow = true;
  group.add(wheels);
  return group;
}

function createPortableToilet(accessible = false) {
  const group = new THREE.Group();
  const width = accessible ? 1.55 : 1.05;
  const shell = new THREE.MeshStandardMaterial({ color: accessible ? 0x2d94c4 : 0xa99d84, roughness: 0.82 });
  box(group, [width, 2.35, 1.25], [0, 1.18, 0], shell);
  box(group, [width * 0.84, 1.92, 0.05], [0, 1.03, 0.635], shell, false);
  box(group, [width * 0.96, 0.2, 1.34], [0, 2.42, 0], MATERIALS.dark, false);
  addTextPanel(group, accessible ? "無障礙廁所" : "流動廁所", accessible ? "♿ ACCESSIBLE" : "WC", [width * 0.76, 0.58], [0, 1.72, 0.667], 0, { background: accessible ? "#145b78" : "#5e5546", accent: accessible ? "#70bde6" : "#d7c59e" });
  box(group, [0.07, 0.3, 0.08], [width * 0.28, 0.95, 0.69], MATERIALS.dark, false);
  return group;
}

function createDisplayStall() {
  const group = new THREE.Group();
  const frameLines = [
    [[-1.7, 0, 0], [-1.7, 2.65, 0]], [[1.7, 0, 0], [1.7, 2.65, 0]],
    [[-1.7, 2.65, 0], [1.7, 2.65, 0]], [[-1.7, 0, 0], [-2.2, 0, 1.2]], [[1.7, 0, 0], [2.2, 0, 1.2]],
    [[-2.2, 0, 1.2], [-1.7, 1.25, 0]], [[2.2, 0, 1.2], [1.7, 1.25, 0]],
  ];
  group.add(mergedRods(frameLines, MATERIALS.paleWood, 0.09));
  for (let y = 0.45; y <= 1.35; y += 0.45) box(group, [3.25, 0.09, 0.72], [0, y, 0.35], MATERIALS.paleWood);
  addTextPanel(group, "永續選物", "REUSABLE DISPLAY", [3.1, 0.7], [0, 2.24, 0.04], 0, { background: "#082d57", accent: "#61aa45" });
  return group;
}

function createRestAreas() {
  const group = new THREE.Group();
  const configs = [
    { x: -46, z: 18, rotation: 0 },
    { x: 45, z: 10, rotation: Math.PI / 2 },
  ];
  const woodTransforms = [];
  const cushionTransforms = [];
  configs.forEach(config => {
    const placement = { ...config };
    [[-2.1, -1.1, 0], [2.1, -1.1, 0], [-1.2, 2.0, Math.PI / 2], [2.1, 2.0, Math.PI / 2]].forEach(([x, z, rotation]) => {
      for (let level = 0; level < 3; level += 1) {
        const point = localToWorld(placement, [x, 0.12 + level * 0.19, z]);
        woodTransforms.push({ position: point, scale: [2.55, 0.13, 0.78], rotation: placement.rotation + rotation });
      }
      const cushionPoint = localToWorld(placement, [x, 0.72, z]);
      cushionTransforms.push({ position: cushionPoint, scale: [1.55, 0.16, 0.66], rotation: placement.rotation + rotation });
    });
    const tablePoint = localToWorld(placement, [0, 0.5, 0.3]);
    woodTransforms.push({ position: tablePoint, scale: [2.25, 0.15, 1.25], rotation: placement.rotation });
  });
  const wood = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.wood, woodTransforms.length);
  woodTransforms.forEach((item, index) => setInstance(wood, index, item.position, item.scale, item.rotation));
  wood.instanceMatrix.needsUpdate = true;
  wood.castShadow = true;
  wood.receiveShadow = true;
  group.add(wood);
  const cushions = new THREE.InstancedMesh(UNIT_BOX, MATERIALS.orange, cushionTransforms.length);
  cushionTransforms.forEach((item, index) => setInstance(cushions, index, item.position, item.scale, item.rotation));
  cushions.instanceMatrix.needsUpdate = true;
  group.add(cushions);
  return group;
}

function createCrowd() {
  const group = new THREE.Group();
  const count = 46;
  const buildingSupportFootings = [[-12, -37], [-15, -17], [-13, 19], [-14, 34], [17, 14], [18, 36], [8, -13], [8, 0], [8, 13]];
  const bodyGeometry = new THREE.CapsuleGeometry(0.22, 0.72, 3, 7);
  const headGeometry = new THREE.SphereGeometry(0.22, 9, 7);
  const bodyMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8 });
  const headMaterial = new THREE.MeshStandardMaterial({ color: 0xd8a47f, roughness: 0.9 });
  const bodies = new THREE.InstancedMesh(bodyGeometry, bodyMaterial, count);
  const heads = new THREE.InstancedMesh(headGeometry, headMaterial, count);
  const colors = [COLORS.orange, COLORS.sky, COLORS.green, COLORS.navy, 0xe9b7a5, COLORS.yellow];
  for (let i = 0; i < count; i += 1) {
    const angle = i * 2.399;
    const radius = 12 + (i % 7) * 5.2;
    let x = Math.cos(angle) * radius + (i % 3) * 3;
    const z = Math.sin(angle) * radius + 10;
    const nearbyFooting = buildingSupportFootings.find(([supportX, supportZ]) => Math.hypot(x - supportX, z - supportZ) < 1.25);
    if (nearbyFooting) x += x < nearbyFooting[0] ? -2 : 2;
    setInstance(bodies, i, [x, 1.02, z], [1, 1, 1], -angle);
    setInstance(heads, i, [x, 1.75, z], [1, 1, 1]);
    bodies.setColorAt(i, new THREE.Color(colors[i % colors.length]));
  }
  bodies.instanceMatrix.needsUpdate = true;
  bodies.instanceColor.needsUpdate = true;
  heads.instanceMatrix.needsUpdate = true;
  bodies.castShadow = false;
  heads.castShadow = false;
  group.add(bodies, heads);
  return group;
}

function createServiceDesk() {
  const group = new THREE.Group();
  box(group, [4.6, 0.18, 1.35], [0, 0.82, 0], MATERIALS.paleWood);
  box(group, [4.8, 2.55, 0.18], [0, 1.28, 0.72], MATERIALS.navy);
  addTextPanel(group, "大會服務台", "INFORMATION", [4.25, 1.05], [0, 1.55, 0.825], 0, { background: "#082d57", accent: "#70bde6" });
  box(group, [0.65, 0.65, 0.65], [-1.6, 0.33, 0], MATERIALS.orange);
  box(group, [0.65, 0.65, 0.65], [1.6, 0.33, 0], MATERIALS.sky);
  return group;
}

function addGroundZones(group) {
  const zones = [
    { x: -54, z: 1, radius: 8.5, color: 0xbd3f84 },
    { x: -46, z: 18, radius: 7.8, color: 0xef741c },
    { x: 45, z: 10, radius: 8.1, color: 0xef741c },
    { x: 0, z: 51, radius: 7.4, color: 0x75bee1 },
  ];
  zones.forEach(zone => {
    const mesh = new THREE.Mesh(
      new THREE.RingGeometry(zone.radius - 0.18, zone.radius, 48),
      new THREE.MeshBasicMaterial({ color: zone.color, transparent: true, opacity: 0.72, side: THREE.DoubleSide }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(zone.x, 0.035, zone.z);
    group.add(mesh);
  });
}

export function createOptimizedEvent() {
  const group = new THREE.Group();
  group.name = "家的萬象生活節－企畫書配置優化模型";
  const hotspots = [];
  const add = (object, x, z, rotation = 0) => {
    object.position.set(x, 0, z);
    object.rotation.y = rotation;
    group.add(object);
    return object;
  };

  addGroundZones(group);
  add(createWelcomeGate(), 0, 57, 0);
  add(createHousePhotoWall(), 0, 43, 0);
  add(createGameKiosk("品牌記憶翻牌", "#70bde6", "MEMORY GAME"), -9, 44, 0);
  add(createGameKiosk("萬人共築新 LOGO", "#ef5a17", "LOGO PUZZLE"), 9, 44, 0);
  add(createStage(), -54, 1, 0);
  group.add(createRestAreas());
  group.add(createCanopyField());
  add(createFoodTruck(COLORS.green), -54, 25, Math.PI / 2);
  add(createFoodTruck(COLORS.orange), 55, 16, -Math.PI / 2);
  add(createPortableToilet(false), -55, -32, 0);
  add(createPortableToilet(false), -52.2, -32, 0);
  add(createPortableToilet(true), -48.7, -32, 0);
  add(createDisplayStall(), 36, 26, Math.PI);
  add(createDisplayStall(), 42, 25, Math.PI);
  add(createServiceDesk(), 8, 22, Math.PI);
  group.add(createCrowd());

  const points = [[0, 56], [5, 43], [46, 4], [-54, 1], [-46, 18], [-51, -30], [8, 22]];
  points.forEach(([x, z], index) => {
    const hit = new THREE.Mesh(
      new THREE.CylinderGeometry(2.5, 2.5, 0.1, 16),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
    );
    hit.position.set(x, 0.1, z);
    hit.userData.chapter = Math.min(index + 1, 6);
    group.add(hit);
    hotspots.push(hit);
  });

  return { group, hotspots };
}
